import { useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import Popover from "@/components/Popover";

/**
 * Custom select with a readable trigger (text never runs under the arrow) and a
 * portal dropdown. options: [{ value, label, hint?, Icon? }]
 * variant: "bare" (inside the search bar), "field" (form) or "pill".
 */
export default function PillSelect({ value, onChange, options, placeholder = "Select", variant = "field", testId, disabled = false, className = "", minWidth = 240 }) {
  const [open, setOpen] = useState(false);
  const anchor = useRef(null);
  const current = options.find((o) => o.value === value);

  const trigger =
    variant === "bare" ? "w-full flex items-center justify-between gap-3 py-1 text-left"
    : variant === "pill" ? "w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-full bg-white border border-[color:var(--line)] hover:border-[color:var(--maroon)]"
    : "w-full flex items-center justify-between gap-3 px-4 py-3 rounded-[14px] bg-white border border-[color:var(--line)] hover:border-[color:var(--maroon)]";

  return (
    <div className={`relative min-w-0 ${className}`}>
      <button ref={anchor} type="button" disabled={disabled} onClick={() => setOpen(!open)} className={`${trigger} disabled:opacity-50 disabled:cursor-not-allowed`} data-testid={testId} aria-haspopup="listbox" aria-expanded={open}>
        <span className={`min-w-0 truncate text-[16px] ${current ? "text-[color:var(--ink)] font-semibold" : "text-neutral-500"}`}>{current ? current.label : placeholder}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-[color:var(--maroon)] transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      <Popover open={open} anchorRef={anchor} onClose={() => setOpen(false)} minWidth={minWidth} testId={testId ? `${testId}-panel` : undefined}>
        <div className="overflow-y-auto overscroll-contain py-1.5" role="listbox">
          {options.map((o) => (
            <button type="button" key={String(o.value)} role="option" aria-selected={o.value === value} onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-[color:var(--ivory)] ${o.value === value ? "bg-[color:var(--ivory)]" : ""}`} data-testid={testId ? `${testId}-opt-${o.value || "any"}` : undefined}>
              {o.Icon && <o.Icon className="w-4 h-4 text-[color:var(--maroon)] shrink-0"/>}
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] text-[color:var(--ink)] leading-tight">{o.label}</span>
                {o.hint && <span className="block text-xs text-neutral-500 leading-tight mt-0.5">{o.hint}</span>}
              </span>
              {o.value === value && <Check className="w-4 h-4 text-[color:var(--maroon)] shrink-0"/>}
            </button>
          ))}
        </div>
      </Popover>
    </div>
  );
}
